// src/views/Vouchers/VoucherFunction.js
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  InputAdornment,
  IconButton,
  CircularProgress,
  Chip,
  Stack,
  Checkbox,
  ListItemText,
  FormControlLabel,
} from "@mui/material";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import axios from "axios";
import Swal from "sweetalert2";
import VoucherProductModal from "./Modal/VoucherProductModal";
import { getAuthCredentials } from "../../utils/auth";

const computeTotal = (arr = []) =>
  arr.reduce((s, { price = 0, quantity = 0 }) => s + price * quantity, 0);

const VoucherFunction = ({
  open,
  handleClose,
  voucher,
  refreshData,
  setIsEditOpen,
  setIsAddOpen,
}) => {
  const { token } = getAuthCredentials();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [products, setProducts] = useState(voucher?.products || []);
  const [updatedVoucher, setUpdatedVoucher] = useState(
    voucher || {
      name: "",
      voucherType: "Full Deduction",
      minimumDeduct: "",
      amountDeduct: "",
      maxDeduct: "",
      voucherCode: "",
      voucherRoleType: "None",
      onlyUseOnce: voucher?.onlyUseOnce ?? false,
    }
  );

  useEffect(() => {
    if (!open) return;
    const fetchRoles = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/access/roles`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setRoles(res.data.roles || []);
      } catch (err) {
        console.error("Failed to fetch roles:", err);
      }
    };
    fetchRoles();
  }, [open, token]);

  useEffect(() => {
    if (updatedVoucher.voucherType === "Product Deduction") {
      setUpdatedVoucher((p) => ({
        ...p,
        amountDeduct: computeTotal(products),
      }));
    }
  }, [products, updatedVoucher.voucherType]);

  /* ───────────── FIELD HANDLERS ───────────── */
  const handleField = (e) =>
    setUpdatedVoucher({ ...updatedVoucher, [e.target.name]: e.target.value });

  const handleGenerateCode = () => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let code = "";
    for (let i = 0; i < 15; i++)
      code += chars[Math.floor(Math.random() * chars.length)];
    setUpdatedVoucher((p) => ({ ...p, voucherCode: code }));
  };

  /* ───────────── ROLE SELECT HELPERS ───────────── */
  const stateToUi = () => {
    if (updatedVoucher.voucherRoleType === "All") return ["All"];
    if (updatedVoucher.voucherRoleType === "None") return ["None"];
    return updatedVoucher.voucherRoleType || [];
  };
  const uiToState = (vals) => {
    if (vals.includes("All")) return "All";
    if (vals.includes("None") || vals.length === 0) return "None";
    return vals;
  };

  const handleRolesChange = (e) => {
    let vals = [...e.target.value];

    if (vals.includes("All")) {
      vals = stateToUi().includes("All") ? [] : ["All"];
    }

    if (vals.length > 1 && vals.includes("All")) {
      vals = vals.filter((v) => v !== "All");
    }

    if (vals.some((v) => v !== "None")) {
      vals = vals.filter((v) => v !== "None");
    }

    if (vals.length === 0) vals = ["None"];

    setUpdatedVoucher({ ...updatedVoucher, voucherRoleType: uiToState(vals) });
  };

  const toggleOnlyOnce = (e) =>
    setUpdatedVoucher({ ...updatedVoucher, onlyUseOnce: e.target.checked });

  /* ───────────── SAVE ───────────── */
  const handleSave = async () => {
    setLoading(true);
    const payload =
    updatedVoucher.voucherType === "Product Deduction"
      ? {
          ...updatedVoucher,
          products,
          minimumDeduct:
            updatedVoucher.minimumDeduct === "" ? 0 : Number(updatedVoucher.minimumDeduct),
          maxDeduct: undefined,
        }
      : {
          ...updatedVoucher,
          minimumDeduct:
            updatedVoucher.minimumDeduct === "" ? 0 : Number(updatedVoucher.minimumDeduct),
        };

    try {
      if (voucher) {
        await axios.patch(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/vouchers/edit-voucher/${voucher._id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/vouchers/create-voucher`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      handleClose();
      Swal.fire("Success!", "Voucher saved.", "success").then(refreshData);
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to save voucher.";
      handleClose();
      Swal.fire("Error!", msg, "error").then(() => {
        setTimeout(
          () => (voucher ? setIsEditOpen(true) : setIsAddOpen(true)),
          200
        );
      });
    } finally {
      setLoading(false);
    }
  };

  /* ───────────── VALIDATION ───────────── */
  const isSaveDisabled = () => {
    if (!updatedVoucher.name?.trim() || !updatedVoucher.voucherCode?.trim())
      return true;
    if (loading) return true;

    if (updatedVoucher.voucherType === "Product Deduction")
      return products.length === 0;

    if (
      updatedVoucher.minimumDeduct === "" ||
      Number(updatedVoucher.minimumDeduct) < 0 ||
      updatedVoucher.amountDeduct === "" ||
      Number(updatedVoucher.amountDeduct) <= 0
    )
      return true;

    if (
      updatedVoucher.voucherType === "Percentage Deduction" &&
      (updatedVoucher.maxDeduct === "" || Number(updatedVoucher.maxDeduct) <= 0)
    )
      return true;

    if (
      updatedVoucher.voucherType === "Product Deduction" &&
      updatedVoucher.minimumDeduct !== "" &&
      Number(updatedVoucher.minimumDeduct) < 0
    ) return true;

    return false;
  };

  /* ───────────── RENDER ───────────── */
  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{voucher ? "Edit Voucher" : "Add Voucher"}</DialogTitle>

        <DialogContent>
          {/* Name & Code */}
          <TextField
            margin="dense"
            label="Name"
            name="name"
            fullWidth
            value={updatedVoucher.name}
            onChange={handleField}
          />
          <TextField
            margin="dense"
            label="Voucher Code"
            name="voucherCode"
            fullWidth
            inputProps={{ maxLength: 15, pattern: "^[a-zA-Z0-9]*$" }}
            value={updatedVoucher.voucherCode}
            onChange={handleField}
            helperText="15 alphanumeric characters"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Generate Code">
                    <IconButton onClick={handleGenerateCode} edge="end">
                      <ShuffleIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />

          {/* Voucher Type */}
          <FormControl margin="dense" fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              name="voucherType"
              value={updatedVoucher.voucherType}
              onChange={handleField}
            >
              <MenuItem value="Full Deduction">Full Deduction</MenuItem>
              <MenuItem value="Percentage Deduction">
                Percentage Deduction
              </MenuItem>
              <MenuItem value="Product Deduction">Product Deduction</MenuItem>
            </Select>
          </FormControl>

          {/* Allowed Roles */}
          <FormControl margin="dense" fullWidth>
            <InputLabel>Allowed Roles</InputLabel>
            <Select
              multiple
              value={stateToUi()}
              onChange={handleRolesChange}
              renderValue={(sel) => {
                if (sel.includes("All")) return "All Roles";
                if (sel.includes("None") || sel.length === 0) return "Customer";
                return roles
                  .filter((r) => sel.includes(r._id))
                  .map((r) => r.name)
                  .join(", ");
              }}
            >
              <MenuItem value="None">
                <Checkbox checked={stateToUi().includes("None")} />
                <ListItemText primary="Customer (default)" />
              </MenuItem>
              <MenuItem value="All">
                <Checkbox checked={stateToUi().includes("All")} />
                <ListItemText primary="All Roles" />
              </MenuItem>
              {roles.map((role) => (
                <MenuItem key={role._id} value={role._id}>
                  <Checkbox checked={stateToUi().includes(role._id)} />
                  <ListItemText primary={role.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Use Once */}
          {updatedVoucher.voucherRoleType !== "None" && (
            <FormControlLabel
              sx={{ mt: 1}}
              control={
                <Checkbox
                  checked={updatedVoucher.onlyUseOnce}
                  onChange={toggleOnlyOnce}
                />
              }
              label="Use Once (per day)"
            />
          )}

          {/* Deduction fields for non-product vouchers */}
          {updatedVoucher.voucherType !== "Product Deduction" && (
            <>
              <TextField
                margin="dense"
                label="Minimum Purchase"
                name="minimumDeduct"
                type="number"
                fullWidth
                value={updatedVoucher.minimumDeduct}
                onChange={handleField}
              />
              <Tooltip
                title={
                  updatedVoucher.voucherType === "Full Deduction"
                    ? "Exact amount to deduct"
                    : "Percentage to deduct"
                }
                arrow
              >
                <TextField
                  margin="dense"
                  label="Amount Deduct"
                  name="amountDeduct"
                  type="number"
                  fullWidth
                  value={updatedVoucher.amountDeduct}
                  onChange={handleField}
                  InputProps={{
                    endAdornment:
                      updatedVoucher.voucherType === "Percentage Deduction" ? (
                        <InputAdornment position="end">%</InputAdornment>
                      ) : null,
                  }}
                />
              </Tooltip>
              <TextField
                margin="dense"
                label="Max Deduct"
                name="maxDeduct"
                type="number"
                fullWidth
                value={updatedVoucher.maxDeduct}
                onChange={handleField}
                disabled={updatedVoucher.voucherType !== "Percentage Deduction"}
              />
            </>
          )}

          {/* Product voucher UI */}
          {updatedVoucher.voucherType === "Product Deduction" && (
            <>
              <Button
                variant="outlined"
                sx={{ mt: 1, display: "block" }}
                onClick={() => setShowProductModal(true)}
              >
                {products.length
                  ? `Edit Products (${products.length})`
                  : "Select Products"}
              </Button>
              {products.length > 0 && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mt: 1, flexWrap: "wrap" }}
                >
                  {products.map((p) => (
                    <Chip
                      key={p.menuItemId}
                      label={`${p.menuName} × ${p.quantity}`}
                      sx={{ mb: 0.5 }}
                    />
                  ))}
                </Stack>
              )}

              <TextField
                margin="dense"
                label="Minimum Purchase"
                name="minimumDeduct"
                type="number"
                fullWidth
                value={updatedVoucher.minimumDeduct}
                onChange={handleField}
                helperText="Set 0 for no minimum purchase requirement"
              />

              <TextField
                margin="dense"
                label="Total Deduction (auto)"
                fullWidth
                disabled
                value={updatedVoucher.amountDeduct}
              />
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            disabled={isSaveDisabled()}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* nested product selector */}
      <VoucherProductModal
        open={showProductModal}
        token={token}
        initialProducts={products}
        onSave={setProducts}
        onClose={() => setShowProductModal(false)}
      />
    </>
  );
};

export default VoucherFunction;
